import { z } from 'zod';
import { weekdayOf } from './schedule';

/**
 * "Einfach sagen" (Magic Add): a sentence, a voice note or a photo becomes suggestions the family
 * checks before anything is saved. The AI answers in one flat JSON shape (easy for any model);
 * `parseMagic` turns it into typed items and quietly drops what does not make sense.
 */

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** "9:00", "09.00", "9 Uhr" → "09:00"; anything else → undefined. */
export function normalizeTime(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const m = /^(\d{1,2})(?:[:.](\d{2}))?(?:\s*Uhr)?$/i.exec(v.trim());
  if (!m) return undefined;
  const h = Number(m[1]);
  const min = Number(m[2] ?? '0');
  if (h > 23 || min > 59) return undefined;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

const text = (max: number) => z.string().trim().min(1).max(max);
const names = z.array(z.string().trim().min(1).max(40)).max(20).default([]);

export const MagicEvent = z.object({
  kind: z.literal('event'),
  title: text(120),
  date: IsoDate,
  endDate: IsoDate.optional(),
  start: z.string().optional(),
  end: z.string().optional(),
  /** First names; empty = the whole family. */
  who: names,
  bring: z.array(text(40)).max(12).default([]),
  location: z.string().trim().max(120).optional(),
  /** "erinnere mich eine Stunde vorher" (1.13): minutes before, or 'eve' for the evening before */
  remind: z.union([z.literal('eve'), z.number().int().min(5).max(1440)]).optional(),
  /**
   * "jeden Dienstag", "alle zwei Wochen", "jeden 3. Montag" (1.27, Mike 26.10.: "dass Serientermine
   * erstellt werden"): the same series as the event form (CalendarEvent.repeat, series.ts).
   */
  repeat: z
    .object({
      days: z.array(z.number().int().min(1).max(7)).max(7),
      freq: z.enum(['daily', 'weekly', 'monthly', 'yearly']).optional(),
      every: z.number().int().min(1).max(12).optional(),
      monthBy: z.enum(['date', 'weekday']).optional(),
      until: IsoDate.optional(),
      count: z.number().int().min(1).max(500).optional(),
      skipHolidays: z.boolean().optional(),
    })
    .optional(),
});
export const MagicTodo = z.object({
  kind: z.literal('todo'),
  title: text(80),
  due: IsoDate.optional(),
  who: names,
});
export const MagicShopping = z.object({
  kind: z.literal('shopping'),
  name: text(80),
  spec: z.string().trim().max(80).optional(),
});
export const MagicNote = z.object({
  kind: z.literal('note'),
  text: text(280),
  until: IsoDate.optional(),
});
export const MagicTimetableDay = z.object({
  weekday: z.number().int().min(1).max(5),
  start: z.string(),
  end: z.string(),
  lessons: z.array(text(40)).max(12).default([]),
});
export const MagicTimetable = z.object({
  kind: z.literal('timetable'),
  /** The child, if the photo or the text says whose plan it is. */
  who: z.string().trim().max(40).optional(),
  label: z.string().trim().max(30).optional(),
  days: z.array(MagicTimetableDay).min(1).max(5),
});

/** A recurring chore with stars (for the kids) – parents only. */
export const MagicTask = z.object({
  kind: z.literal('task'),
  title: text(80),
  who: names,
  stars: z.number().int().min(1).max(100).default(2),
  daypart: z.enum(['morning', 'afternoon', 'evening', 'anytime']).default('anytime'),
  /** 1 = Monday … 7 = Sunday; missing = every day */
  days: z.array(z.number().int().min(1).max(7)).max(7).optional(),
  /** only once, on this day */
  date: IsoDate.optional(),
});

/** Freizeit (1.10): a trip idea for later. */
export const MagicIdea = z.object({
  kind: z.literal('idea'),
  title: text(80),
  place: z.enum(['out', 'in', 'any']).default('any'),
});
/** A paper voucher – typed or read from a photo. */
export const MagicVoucher = z.object({
  kind: z.literal('voucher'),
  title: text(80),
  value: z.string().trim().max(40).optional(),
  until: IsoDate.optional(),
  code: z.string().trim().max(60).optional(),
  from: z.string().trim().max(40).optional(),
});
/** A wish for somebody's wish list (no name = the family's shared wishes). */
export const MagicWish = z.object({
  kind: z.literal('wish'),
  title: text(80),
  who: names,
  price: z.string().trim().max(30).optional(),
});

/**
 * Change or delete something that exists ("Lösch den Zahnarzt am Dienstag", "Fußball ist heute
 * erst um 18 Uhr"). The AI only says what is meant – the app finds it on the device, so the
 * family's calendar never goes to the AI.
 */
export const MagicEdit = z.object({
  kind: z.literal('edit'),
  action: z.enum(['change', 'delete']),
  what: z.enum(['event', 'todo', 'task', 'note']),
  /** how the family called the existing entry */
  target: text(120),
  /** the day of the existing entry, if named */
  targetDate: IsoDate.optional(),
  /** change: the new values */
  set: z
    .object({
      title: text(120).optional(),
      date: IsoDate.optional(),
      start: z.string().optional(),
      end: z.string().optional(),
      who: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
    })
    .default({}),
});

export const MagicItem = z.discriminatedUnion('kind', [
  MagicEvent,
  MagicTodo,
  MagicShopping,
  MagicNote,
  MagicTimetable,
  MagicTask,
  MagicEdit,
  MagicIdea,
  MagicVoucher,
  MagicWish,
]);
export type MagicItem = z.infer<typeof MagicItem>;
export type MagicEvent = z.infer<typeof MagicEvent>;
export type MagicTimetable = z.infer<typeof MagicTimetable>;
export type MagicTask = z.infer<typeof MagicTask>;
export type MagicEdit = z.infer<typeof MagicEdit>;
export type MagicVoucher = z.infer<typeof MagicVoucher>;

/**
 * JSON schema the AI must answer in: one list of flat items with a `kind`. Kept deliberately
 * simple (no unions) – small free models follow flat schemas best.
 */
export const MAGIC_RESPONSE_SCHEMA = {
  type: 'object',
  properties: {
    items: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          action: {
            type: 'string',
            enum: ['add', 'change', 'delete'],
            description: 'add = new entry (default); change/delete = an existing one',
          },
          kind: {
            type: 'string',
            enum: [
              'event',
              'todo',
              'task',
              'shopping',
              'note',
              'timetable',
              'idea',
              'voucher',
              'wish',
            ],
          },
          target_title: {
            type: 'string',
            description: 'change/delete: name of the existing entry, e.g. "Zahnarzt"',
          },
          target_date: {
            type: 'string',
            description: 'change/delete: day of the existing entry YYYY-MM-DD, if named',
          },
          title: {
            type: 'string',
            description: 'event/todo/task: short title, no names or times (change: new title)',
          },
          date: { type: 'string', description: 'event: YYYY-MM-DD (change: new day)' },
          stars: { type: 'integer', description: 'task: stars as reward, default 2' },
          daypart: {
            type: 'string',
            enum: ['morning', 'afternoon', 'evening', 'anytime'],
            description: 'task: time of day',
          },
          repeatDays: {
            type: 'array',
            items: { type: 'integer' },
            description:
              'task and weekly event: weekdays 1 = Monday … 7 = Sunday; task: empty = every day',
          },
          repeatFreq: {
            type: 'string',
            enum: ['daily', 'weekdays', 'weekly', 'monthly', 'yearly'],
            description:
              'event: only for a repeating event ("jeden Dienstag", "täglich", "monatlich")',
          },
          repeatEvery: {
            type: 'integer',
            description:
              'event series: every n-th day/week/month/year ("alle 2 Wochen" = 2), default 1',
          },
          repeatMonthBy: {
            type: 'string',
            enum: ['date', 'weekday'],
            description: 'monthly event: same date ("am 15.") or same weekday ("jeden 3. Montag")',
          },
          repeatUntil: {
            type: 'string',
            description: 'event series: last day YYYY-MM-DD, if said',
          },
          repeatCount: { type: 'integer', description: 'event series: number of dates, if said' },
          skipHolidays: {
            type: 'boolean',
            description: 'event series: pause in the school holidays ("nicht in den Ferien")',
          },
          endDate: { type: 'string', description: 'event over several days: last day YYYY-MM-DD' },
          start: { type: 'string', description: 'event: HH:MM, omit for all-day' },
          end: { type: 'string', description: 'event: HH:MM' },
          due: { type: 'string', description: 'todo: YYYY-MM-DD' },
          who: {
            type: 'array',
            items: { type: 'string' },
            description: 'first names from the family list; empty = whole family',
          },
          bring: { type: 'array', items: { type: 'string' }, description: 'event: things to take' },
          location: { type: 'string' },
          remindMinutes: {
            type: 'integer',
            description:
              'event: remind this many minutes before, only if asked ("erinnere mich …")',
          },
          remindEve: {
            type: 'boolean',
            description: 'event: remind the evening before, only if asked',
          },
          name: { type: 'string', description: 'shopping: article, e.g. "Milch"' },
          spec: { type: 'string', description: 'shopping: amount, e.g. "2 Liter"' },
          text: { type: 'string', description: 'note: the message for the pinboard' },
          until: {
            type: 'string',
            description: 'note: YYYY-MM-DD until when it matters; voucher: valid until',
          },
          place: {
            type: 'string',
            enum: ['out', 'in', 'any'],
            description: 'idea: out = outdoors, in = indoors (also when it rains), any',
          },
          value: { type: 'string', description: 'voucher: worth, e.g. "25 €" or "2 Personen"' },
          code: { type: 'string', description: 'voucher: code printed on it' },
          from: { type: 'string', description: 'voucher: from whom' },
          price: { type: 'string', description: 'wish: price if named, e.g. "ca. 60 €"' },
          label: { type: 'string', description: 'timetable: "Schule" or "Kita"' },
          days: {
            type: 'array',
            description: 'timetable: one entry per school day',
            items: {
              type: 'object',
              properties: {
                weekday: { type: 'integer', description: '1 = Monday … 5 = Friday' },
                start: { type: 'string', description: 'HH:MM first lesson' },
                end: { type: 'string', description: 'HH:MM last lesson ends' },
                lessons: { type: 'array', items: { type: 'string' } },
              },
              required: ['weekday', 'start', 'end'],
            },
          },
        },
        required: ['kind'],
      },
    },
  },
  required: ['items'],
} as const;

/** Whatever the AI answered → valid items only (times normalized, invalid entries dropped). */
export function parseMagic(raw: unknown): MagicItem[] {
  const list = (raw as { items?: unknown })?.items;
  if (!Array.isArray(list)) return [];
  const out: MagicItem[] = [];
  for (const entry of list.slice(0, 40)) {
    if (!entry || typeof entry !== 'object') continue;
    const e = { ...(entry as Record<string, unknown>) };
    // strip empty strings the model likes to send for "not set"
    for (const k of Object.keys(e)) if (e[k] === '' || e[k] === null) delete e[k];
    if (e.action === 'change' || e.action === 'delete') {
      const set =
        e.action === 'change'
          ? Object.fromEntries(
              Object.entries({
                // a title equal to the target is no change
                title: e.target_title && e.title !== e.target_title ? e.title : undefined,
                date: e.date,
                start: normalizeTime(e.start),
                end: normalizeTime(e.end),
                who: Array.isArray(e.who) && e.who.length ? e.who : undefined,
              }).filter(([, v]) => v !== undefined),
            )
          : {};
      const parsed = MagicEdit.safeParse({
        kind: 'edit',
        action: e.action,
        what: e.kind,
        target: e.target_title ?? e.title,
        targetDate: e.target_date,
        set,
      });
      if (parsed.success) out.push(parsed.data);
      continue;
    }
    delete e.action;
    delete e.target_title;
    delete e.target_date;
    if (e.kind === 'task') {
      if (Array.isArray(e.repeatDays) && e.repeatDays.length) e.days = e.repeatDays;
      delete e.repeatDays;
    }
    // a voucher without a readable date is still a voucher – the family adds the date
    if (e.kind === 'voucher' && typeof e.until === 'string' && !IsoDate.safeParse(e.until).success)
      delete e.until;
    if (e.kind === 'event') {
      e.remind =
        e.remindEve === true
          ? 'eve'
          : typeof e.remindMinutes === 'number'
            ? e.remindMinutes
            : undefined;
      if (e.remind === undefined) delete e.remind;
      delete e.remindMinutes;
      delete e.remindEve;
      e.start = normalizeTime(e.start);
      e.end = e.start ? normalizeTime(e.end) : undefined;
      if (e.endDate && e.endDate === e.date) delete e.endDate;
      const series = seriesOf(e);
      if (series) e.repeat = series;
      for (const k of [
        'repeatFreq',
        'repeatDays',
        'repeatEvery',
        'repeatMonthBy',
        'repeatUntil',
        'repeatCount',
        'skipHolidays',
      ])
        delete e[k];
    }
    if (e.kind === 'timetable' && Array.isArray(e.days)) {
      e.days = (e.days as Record<string, unknown>[])
        .map((d) => ({ ...d, start: normalizeTime(d.start), end: normalizeTime(d.end) }))
        .filter((d) => d.start && d.end);
    }
    const parsed = MagicItem.safeParse(e);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

/**
 * The AI's flat series fields → CalendarEvent.repeat, stored like the event form does: daily and
 * "werktags" as weekly on all days / Mon–Fri (series.ts), weekly without days on the start's weekday.
 */
function seriesOf(e: Record<string, unknown>): MagicEvent['repeat'] {
  const freq = e.repeatFreq;
  if (typeof freq !== 'string' || typeof e.date !== 'string' || !IsoDate.safeParse(e.date).success)
    return undefined;
  const every =
    typeof e.repeatEvery === 'number' && e.repeatEvery > 1
      ? Math.min(12, Math.round(e.repeatEvery))
      : undefined;
  const days = (Array.isArray(e.repeatDays) ? e.repeatDays : [])
    .filter((d): d is number => Number.isInteger(d) && (d as number) >= 1 && (d as number) <= 7)
    .sort();
  const base = {
    ...(typeof e.repeatUntil === 'string' &&
    IsoDate.safeParse(e.repeatUntil).success &&
    e.repeatUntil > e.date
      ? { until: e.repeatUntil }
      : {}),
    ...(typeof e.repeatCount === 'number' && e.repeatCount >= 1
      ? { count: Math.min(500, Math.round(e.repeatCount)) }
      : {}),
    ...(e.skipHolidays === true ? { skipHolidays: true } : {}),
  };
  switch (freq) {
    case 'daily':
      return every
        ? { ...base, freq: 'daily', days: [], every }
        : { ...base, freq: 'weekly', days: [1, 2, 3, 4, 5, 6, 7] };
    case 'weekdays':
      return { ...base, freq: 'weekly', days: [1, 2, 3, 4, 5] };
    case 'weekly':
      return {
        ...base,
        freq: 'weekly',
        days: days.length ? [...new Set(days)] : [weekdayOf(e.date)],
        ...(every ? { every } : {}),
      };
    case 'monthly':
      return {
        ...base,
        freq: 'monthly',
        days: [],
        monthBy: e.repeatMonthBy === 'weekday' ? 'weekday' : 'date',
        ...(every ? { every } : {}),
      };
    case 'yearly':
      return { ...base, freq: 'yearly', days: [] };
  }
  return undefined;
}

const WEEKDAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

/**
 * The instructions for the AI. Only what it needs: today's date and the family's first names
 * (to recognise "Lotta"). Never birthdays, addresses, stars or anything from the calendar.
 */
export function magicInstructions(
  today: string,
  family: { name: string; kid: boolean }[],
  locale: 'de' | 'en' = 'de',
) {
  const who = family.map((m) => `${m.name}${m.kid ? ' (Kind)' : ''}`).join(', ');
  return [
    'Du hilfst einer Familie, Dinge in ihre Familien-App einzutragen. Antworte nur mit JSON nach dem Schema.',
    `Heute ist ${WEEKDAYS[weekdayOf(today) - 1]}, ${today}. Zeitzone Europe/Berlin.`,
    `Familie: ${who || 'unbekannt'}.`,
    'Erkenne alles, was eingetragen, geändert oder gelöscht werden soll, und lege für jedes Ding genau ein Element an (action "add", wenn nichts anderes gesagt wird):',
    '- event: Termin mit Datum (relative Angaben wie "nächsten Dienstag" in ein Datum umrechnen), optional Uhrzeit, wer, was mitnehmen, Ort. Nur wenn ausdrücklich gesagt: remindMinutes ("erinnere mich eine Stunde vorher" = 60) oder remindEve ("am Vorabend erinnern").',
    '  Wiederholt sich ein Termin ("jeden Dienstag", "immer montags und mittwochs", "alle zwei Wochen", "jeden 3. Montag im Monat", "jährlich"), ist es EIN event als Serie: date = der erste Termin ab heute, repeatFreq (daily, weekdays, weekly, monthly, yearly), bei weekly repeatDays, repeatEvery bei "alle n", bei monthly repeatMonthBy (date = "am 15.", weekday = "jeden 3. Montag"), repeatUntil oder repeatCount nur wenn gesagt, skipHolidays bei "nicht in den Ferien". Keine einzelnen Termine für jede Woche anlegen.',
    // 1.28: the app calls both "Aufgabe" – the word alone no longer means stars (Mike, 27.10.)
    '- todo: einmalige Aufgabe ohne Sterne ("Geschenk besorgen", "Aufgabe: Klassenfahrt überweisen"). Auch wenn jemand "Aufgabe" sagt: einmalig und ohne Sterne ist es ein todo.',
    '- task: wiederkehrende Aufgabe/Pflicht mit Sternen als Belohnung, meist für Kinder ("Lotta soll jeden Tag den Hund füttern", "Jonas bringt samstags den Müll raus, 5 Sterne"). Eine einmalige Aufgabe ist nur dann eine task, wenn Sterne genannt werden – dann date = der Tag.',
    '- shopping: einzelner Artikel zum Einkaufen, Menge in spec ("Milch", "2 Liter"). Mehrere Artikel = mehrere Elemente.',
    // Mike, 26.10.: "Wenn nicht explizit Notiz erwähnt wird, ist es auch okay ein Terminvorschlag
    // daraus zu machen" – every model read "Oma kommt am Sonntag zum Kaffee" as an event anyway
    '- note: nur wenn ausdrücklich eine Notiz oder die Pinnwand gemeint ist ("Notiz: Schlüssel liegt beim Nachbarn", "Schreib an die Pinnwand, dass Oma Sonntag kommt"). Hat etwas einen Tag oder eine Uhrzeit und ist keine Notiz verlangt, ist es ein event.',
    '- idea: Ausflugs- oder Freizeitidee für irgendwann ("Wir wollten mal in den Kletterpark"); place out = draußen, in = drinnen/bei Regen, sonst any.',
    '- voucher: Gutschein, auch auf einem Foto: title = was und wo (Geschäft, Ort), value = Wert, until = gültig bis (YYYY-MM-DD, "Ende Mai" = letzter Tag), code = Gutscheincode, from = von wem. Datum und Code nur, wenn sie wirklich dastehen.',
    '- wish: Wunsch für den Wunschzettel ("Lotta wünscht sich ein Fahrrad"); who = wer sich das wünscht (leer = die ganze Familie), price falls genannt.',
    '- timetable: nur bei einem Stundenplan (Foto) – pro Schultag Beginn, Ende und Fächer der Reihe nach.',
    'Bei einem Foto eines Papierkalenders: jeden eingetragenen Termin als event, mit dem Monat und Jahr vom Foto.',
    'Ändern oder Löschen eines bestehenden Eintrags ("lösch …", "verschieb …", "… ist erst um 18 Uhr", "streich …"): action "change" oder "delete", kind = Art des Eintrags, target_title = sein Name, target_date = sein Tag, falls genannt. Bei change nur die neuen Werte setzen (z. B. start, date).',
    'Namen in "who" nur aus der Familienliste. Titel kurz und ohne Namen, Uhrzeiten oder Datum.',
    'Der Text kann von einer Spracherkennung stammen: Korrigiere offensichtliche Hörfehler, vor allem bei Namen aus der Familienliste (ähnlich klingend = dieser Name), Uhrzeiten ("viertel nach fünf" = 17:15) und Wochentagen.',
    'Erfinde nichts. Wenn nichts zum Eintragen da ist, antworte mit {"items": []}.',
    // Familiensprache (1.19): titles and texts in the family's language, whatever was typed
    locale === 'en'
      ? 'Schreibe alle Titel und Texte auf Englisch (britisches Englisch), auch wenn die Eingabe deutsch ist.'
      : 'Schreibe alle Titel und Texte auf Deutsch, auch wenn die Eingabe englisch ist.',
  ].join('\n');
}

export const simplify = (s: string) =>
  s
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Paper calendar "abgleichen": is this suggested event already in the family calendar? Same day
 * and a similar title (one contains the other, or the same first word) = already there; same
 * day, similar title but another time = changed on paper.
 */
/** Do two titles mean the same thing? One contains the other, or they start with the same word. */
export function similarTitle(a: string, b: string): boolean {
  const t = simplify(a);
  const o = simplify(b);
  return !!t && !!o && (t.includes(o) || o.includes(t) || t.split(' ')[0] === o.split(' ')[0]);
}

export function matchEvent(
  item: Pick<MagicEvent, 'title' | 'date' | 'start'>,
  existing: { title: string; date: string; start?: string }[],
): { status: 'new' } | { status: 'same' | 'otherTime'; start?: string } {
  for (const e of existing) {
    if (e.date !== item.date || !similarTitle(item.title, e.title)) continue;
    if (!item.start || !e.start || item.start === e.start) return { status: 'same' };
    return { status: 'otherTime', start: e.start };
  }
  return { status: 'new' };
}

// ---------- callables ----------

export const MagicAddInput = z
  .object({
    text: z.string().trim().max(2000).optional(),
    /** what the photo is, when the app knows it – picks the AI model (1.27) */
    purpose: z.enum(['voucher']).nullish(),
    image: z
      .object({
        mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
        /** base64, already scaled down on the device (≈ 1600 px) */
        data: z.string().min(16).max(8_000_000),
      })
      .optional(),
  })
  .refine((v) => Boolean(v.text) || Boolean(v.image), {
    message: 'Bitte etwas schreiben, sagen oder ein Foto wählen.',
  });
export type MagicAddInput = z.infer<typeof MagicAddInput>;
